import axios from "axios";
import { apiUrl } from "@/config/env";
import { api } from "@/services/apiClient";
import type { AuthUser } from "@/types";

export type TokenResponse = {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  user?: Record<string, unknown>;
};

export type Invitation = {
  id: string;
  token: string;
  email: string;
  role: string;
  status: string;
  expires_at?: string;
  created_at?: string;
};

export type TeamMember = {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar_url?: string;
  is_active: boolean;
  has_google: boolean;
  created_at: string;
};

export type OrganizationProfile = {
  ok: boolean;
  tenant_id: string;
  organization_name: string;
  industry_id: string;
  member_count: number;
};

function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return {};
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** 3. Existing Users: Native MarketIQ Login. */
export async function passwordLogin(usernameOrEmail: string, password: string): Promise<TokenResponse> {
  const loginUrl = apiUrl("/api/marketing/auth/login");
  try {
    const { data } = await axios.post(
      loginUrl,
      {
        email: usernameOrEmail,
        password,
        username: usernameOrEmail,
      },
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    return data as TokenResponse;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      if (err.code === "ERR_NETWORK" || err.message.includes("Network Error")) {
        throw new Error("Cannot reach MarketIQ backend at " + loginUrl);
      }
      const detail = (err.response?.data as { detail?: string })?.detail;
      if (detail) throw new Error(detail);
    }
    throw err;
  }
}

/** 1. Manual Signup: Name, Email, Password, Company Name -> creates organization & admin. */
export async function manualSignup(params: {
  name: string;
  email: string;
  password: string;
  company_name: string;
  industry_id?: string;
  invitation_token?: string;
}): Promise<TokenResponse> {
  const signupUrl = apiUrl("/api/marketing/auth/signup");
  try {
    const { data } = await axios.post(signupUrl, params, {
      headers: { "Content-Type": "application/json" },
    });
    return data as TokenResponse;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      if (err.code === "ERR_NETWORK" || err.message.includes("Network Error")) {
        throw new Error("Cannot reach MarketIQ backend at " + signupUrl);
      }
      const detail = (err.response?.data as { detail?: string })?.detail;
      if (detail) throw new Error(detail);
    }
    throw err;
  }
}

export type PasswordResetRequestResult = {
  ok: boolean;
  message: string;
  /** Development-only: present when SMTP is not configured. */
  dev_reset_link?: string;
  dev_note?: string;
};

export type PasswordResetTokenState = {
  ok: boolean;
  state: "ok" | "unknown" | "used" | "expired";
  message: string;
};

/**
 * Forgot password: ask for a reset link. Local (password) accounts get an email;
 * Google-only accounts have no local password. The answer is always the same
 * generic message so this cannot be used to discover which emails have accounts.
 */
export async function requestPasswordReset(email: string): Promise<PasswordResetRequestResult> {
  const url = apiUrl("/api/marketing/auth/forgot-password");
  try {
    const { data } = await axios.post(
      url,
      { email },
      { headers: { "Content-Type": "application/json" } },
    );
    return data as PasswordResetRequestResult;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      if (err.code === "ERR_NETWORK" || err.message.includes("Network Error")) {
        throw new Error("Cannot reach MarketIQ backend at " + url);
      }
      const detail = (err.response?.data as { detail?: string })?.detail;
      if (detail) throw new Error(detail);
    }
    throw err;
  }
}

/** Check a reset link before showing the new-password form. */
export async function verifyPasswordResetToken(token: string): Promise<PasswordResetTokenState> {
  const url = apiUrl("/api/marketing/auth/reset-password/verify");
  try {
    const { data } = await axios.get(url, { params: { token } });
    return data as PasswordResetTokenState;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const detail = (err.response?.data as { detail?: string })?.detail;
      if (detail) throw new Error(detail);
    }
    throw err;
  }
}

/** Finish the reset with the single-use token from the email link. */
export async function resetPassword(
  token: string,
  newPassword: string,
): Promise<{ ok: boolean; message: string }> {
  const url = apiUrl("/api/marketing/auth/reset-password");
  try {
    const { data } = await axios.post(
      url,
      { token, new_password: newPassword },
      { headers: { "Content-Type": "application/json" } },
    );
    return data as { ok: boolean; message: string };
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const detail = (err.response?.data as { detail?: string })?.detail;
      if (detail) throw new Error(detail);
    }
    throw err;
  }
}

/** 2. Google OAuth: Get Google authorization URL. */
export async function getGoogleAuthUrl(returnTo?: string, inviteToken?: string): Promise<{ authorize_url: string; redirect_uri: string }> {
  const url = apiUrl("/api/marketing/auth/google/url");
  const { data } = await axios.get(url, {
    params: { return_to: returnTo, invite_token: inviteToken },
  });
  return data;
}

/** 2. Google Onboarding: First-time user creates Organization with explicit Company Name. */
export async function completeGoogleOnboarding(params: {
  onboarding_token: string;
  company_name: string;
  industry_id?: string;
}): Promise<TokenResponse> {
  const url = apiUrl("/api/marketing/auth/google/onboard");
  try {
    const { data } = await axios.post(url, params, {
      headers: { "Content-Type": "application/json" },
    });
    return data as TokenResponse;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const detail = (err.response?.data as { detail?: string })?.detail;
      if (detail) throw new Error(detail);
    }
    throw err;
  }
}

/** Google verification or dev test simulation. */
export async function verifyGoogleCredential(params: {
  credential?: string;
  simulate_email?: string;
  simulate_name?: string;
  invitation_token?: string;
}): Promise<{ is_existing: boolean; token?: TokenResponse; onboarding_token?: string; email?: string; name?: string }> {
  const url = apiUrl("/api/marketing/auth/google/verify");
  try {
    const { data } = await axios.post(url, params, {
      headers: { "Content-Type": "application/json" },
    });
    return data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const detail = (err.response?.data as { detail?: string })?.detail;
      if (detail) throw new Error(detail);
    }
    throw err;
  }
}

/** 4. Invitation Management: Create invitation (Admin only). */
export async function createInvitation(email: string, role: string = "member"): Promise<{ ok: boolean; invitation: Invitation; invite_url: string; message: string }> {
  const { data } = await api.post("/api/marketing/auth/invitations", { email, role });
  return data;
}

/** 4. Invitation Management: List pending invitations for organization. */
export async function listInvitations(): Promise<Invitation[]> {
  const { data } = await api.get("/api/marketing/auth/invitations");
  return data.invitations || [];
}

/** 4. Invitation Management: Revoke invitation. */
export async function revokeInvitation(inviteId: string): Promise<boolean> {
  const { data } = await api.delete(`/api/marketing/auth/invitations/${inviteId}`);
  return Boolean(data.ok);
}

/** 4. Invitation Verification: Public verify invite token. */
export async function verifyInvitation(token: string): Promise<{
  ok: boolean;
  email: string;
  organization_name: string;
  role: string;
  expires_at?: string;
}> {
  const url = apiUrl("/api/marketing/auth/invitations/verify");
  const { data } = await axios.get(url, { params: { token } });
  return data;
}

/** 4. Invitation Acceptance: Accept invitation via password or Google. */
export async function acceptInvitation(params: {
  token: string;
  name?: string;
  password?: string;
  onboarding_token?: string;
}): Promise<TokenResponse> {
  const url = apiUrl("/api/marketing/auth/invitations/accept");
  try {
    const { data } = await axios.post(url, params, {
      headers: { "Content-Type": "application/json" },
    });
    return data as TokenResponse;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const detail = (err.response?.data as { detail?: string })?.detail;
      if (detail) throw new Error(detail);
    }
    throw err;
  }
}

/** Team Members: list members in tenant. */
export async function fetchTeamMembers(): Promise<TeamMember[]> {
  const { data } = await api.get("/api/marketing/auth/team");
  return data.members || [];
}

/** Organization profile. */
export async function fetchOrganizationProfile(): Promise<OrganizationProfile> {
  const { data } = await api.get("/api/marketing/auth/organization");
  return data;
}

/**
 * Exchange a refresh token for a new access token.
 *
 * There is no refresh endpoint on the marketing service yet, and a refresh token is NOT an
 * access token: it is signed with the same secret but its payload is only
 * `{sub, type: "refresh"}`. Handing it to the API as a bearer token made the auth edge
 * reject the retried call with 401 "Token is missing tenant_id", which signed the user out
 * with a misleading reason *and* overwrote the still-valid access token. Returning `null`
 * makes the caller sign out cleanly instead of corrupting the session.
 */
export async function refreshAccessToken(refreshToken: string): Promise<TokenResponse | null> {
  void refreshToken;
  return null;
}

/** Change Password for manual logined members. */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: boolean; message: string }> {
  try {
    const { data } = await api.post("/api/marketing/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const detail = (err.response?.data as { detail?: string })?.detail;
      if (detail) throw new Error(detail);
    }
    throw err;
  }
}

export async function fetchCurrentUser(token: string): Promise<AuthUser> {
  const payload = decodeJwtPayload(token);
  const email = String(payload.email || payload.preferred_username || "");

  try {
    const { data } = await api.get("/api/marketing/auth/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
    const u = (data && data.user) || payload;
    const hasGoogle = Boolean(u.has_google ?? payload.has_google);
    const hasPassword = u.has_password !== undefined
      ? Boolean(u.has_password)
      : (payload.has_password !== undefined ? Boolean(payload.has_password) : !hasGoogle);

    return {
      id: String(u.sub || u.user_id || u.id || "usr_user"),
      email: String(u.email || email || "user@marketiq.io"),
      name: String(u.name || email.split("@")[0] || "User"),
      role: String(u.role || "admin"),
      tenantId: String(u.tenant_id || "t_acme_logistics"),
      industryId: String(u.industry_id || "general"),
      organization: String(u.organization || "Acme Logistics"),
      hasPassword,
      hasGoogle,
    };
  } catch {
    const hasGoogle = Boolean(payload.has_google);
    const hasPassword = payload.has_password !== undefined ? Boolean(payload.has_password) : !hasGoogle;
    return {
      id: String(payload.sub || "usr_user"),
      email: email || "user@marketiq.io",
      name: String(payload.name || email.split("@")[0] || "User"),
      role: String(payload.role || "admin"),
      tenantId: String(payload.tenant_id || "t_acme_logistics"),
      industryId: String(payload.industry_id || "general"),
      organization: String(payload.organization || "Acme Logistics"),
      hasPassword,
      hasGoogle,
    };
  }
}
